import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Fingerprint, Menu, X, ArrowRight, ChevronDown } from "lucide-react"
import { Container, Button } from "../_components/ui"
import { META } from "../_data/content"

const NAV_GROUPS = [
  {
    id: "producto",
    label: "Producto",
    items: [
      { to: "/precios",  label: "Precios",    desc: "Planes y tarifas" },
      { to: "/api-docs", label: "API Docs",   desc: "Integración B2B" },
    ],
  },
  {
    id: "recursos",
    label: "Recursos",
    items: [
      { to: "/status",     label: "Estado del sistema", desc: "Uptime y servicios" },
      { to: "/terminos",   label: "Términos",            desc: "Contrato de servicio" },
      { to: "/privacidad", label: "Privacidad",          desc: "Ley 29733" },
    ],
  },
]

export default function Navbar({ user }) {
  const { pathname } = useLocation()
  const isLanding = pathname === "/"
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [openMobileGroup, setOpenMobileGroup] = useState(null)
  const navRef = useRef(null)

  // Scroll rAF (L0.8)
  useEffect(() => {
    let raf = 0
    const read = () => {
      raf = 0
      setScrolled((prev) => {
        const next = window.scrollY > 20
        return next === prev ? prev : next
      })
    }
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(read)
    }
    read()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  // Escape + bloqueo de body con panel mobile abierto
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") setOpen(false) }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  // Click afuera cierra dropdown desktop
  useEffect(() => {
    if (!openDropdown) return
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null)
      }
    }
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null) }
    document.addEventListener("mousedown", onClick)
    window.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      window.removeEventListener("keydown", onKey)
    }
  }, [openDropdown])

  // Cerrar menú al cruzar a desktop
  useEffect(() => {
    if (!open) return
    const mq = window.matchMedia("(min-width: 1024px)")
    const onChange = (e) => { if (e.matches) setOpen(false) }
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [open])

  const onDark = isLanding ? (!scrolled && !open) : true

  return (
    <header
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        open
          ? "bg-white border-b border-surface-200"
          : !isLanding
            ? "bg-gradient-hero border-b border-white/10"
            : scrolled
              ? "bg-white/95 lg:bg-white/85 lg:backdrop-blur-lg border-b border-surface-200"
              : "bg-transparent"
      }`}
    >
      <Container>
        <nav className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" onClick={() => setOpen(false)}>
            <div className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center shadow-card">
              <Fingerprint className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <span className={`font-display font-bold text-lg tracking-tight ${onDark ? "text-white" : "text-surface-900"}`}>
              iDaaS<span className={`ml-1 ${onDark ? "text-teal-300" : "text-teal-700"}`}>Perú</span>
            </span>
          </Link>

          {/* Nav desktop con dropdowns */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_GROUPS.map((g) => {
              const isOpen = openDropdown === g.id
              const active = g.items.some((i) => window.location.pathname.startsWith(i.to))
              return (
                <div key={g.id} className="relative">
                  <button
                    onClick={() => setOpenDropdown(isOpen ? null : g.id)}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    className={`flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                      onDark
                        ? "text-white/85 hover:text-white hover:bg-white/10"
                        : active
                          ? "text-teal-700 bg-teal-50"
                          : "text-surface-600 hover:text-teal-700 hover:bg-teal-50"
                    }`}
                  >
                    {g.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 rounded-xl bg-white border border-surface-200 shadow-elevated overflow-hidden">
                      {g.items.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => setOpenDropdown(null)}
                          className="block px-4 py-3 hover:bg-teal-50 transition-colors border-b border-surface-100 last:border-0"
                        >
                          <div className="text-sm font-semibold text-surface-900">{it.label}</div>
                          <div className="text-xs text-surface-500 mt-0.5">{it.desc}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* CTA desktop */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <Button as={Link} to="/dashboard" variant="primary" size="md">
                Ir al panel <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Link to="/login" className={`text-sm font-semibold px-3 py-2 transition-colors ${onDark ? "text-white/90 hover:text-white" : "text-surface-700 hover:text-teal-700"}`}>
                  Iniciar sesión
                </Link>
                <Button as={Link} to="/register" variant="primary" size="md">
                  {META.ctaRegister} <ArrowRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>

          {/* Hamburger mobile/tablet */}
          <button
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${onDark ? "text-white hover:bg-white/10" : "text-surface-700 hover:bg-surface-100"}`}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
      </Container>

      {/* Panel mobile/tablet con accordion */}
      {open && (
        <div className="lg:hidden bg-white border-b border-surface-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <Container>
            <div className="py-4 flex flex-col gap-1">
              {NAV_GROUPS.map((g) => {
                const isOpen = openMobileGroup === g.id
                return (
                  <div key={g.id}>
                    <button
                      onClick={() => setOpenMobileGroup(isOpen ? null : g.id)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-surface-700 hover:bg-teal-50 hover:text-teal-700 font-medium transition-colors"
                      aria-expanded={isOpen}
                    >
                      {g.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="ml-2 pl-3 border-l-2 border-teal-100 flex flex-col">
                        {g.items.map((it) => (
                          <Link
                            key={it.to}
                            to={it.to}
                            onClick={() => setOpen(false)}
                            className="px-4 py-2.5 rounded-lg text-sm text-surface-600 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                          >
                            {it.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
              <div className="border-t border-surface-200 my-2" />
              {user ? (
                <Button as={Link} to="/dashboard" variant="primary" size="lg" className="w-full" onClick={() => setOpen(false)}>
                  Ir al panel <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <>
                  <Button as={Link} to="/login" variant="outline" size="lg" className="w-full" onClick={() => setOpen(false)}>
                    Iniciar sesión
                  </Button>
                  <Button as={Link} to="/register" variant="primary" size="lg" className="w-full" onClick={() => setOpen(false)}>
                    {META.ctaRegister} <ArrowRight className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </Container>
        </div>
      )}
    </header>
  )
}
